import { Input, Button } from '@tkpod/ui';
import type { OrderLineItem } from '@/types/order-wizard';
import { OptionUploadCards } from './OptionUploadCards';

interface ShippingAddressForm {
  name: string;
  address1: string;
  address2: string;
  city: string;
  province: string;
  zip: string;
  country: string;
  phone: string;
}

interface ReviewStepProps {
  items: OrderLineItem[];
  customerName: string;
  customerEmail: string;
  customerPhone: string;
  notes: string;
  shippingAddress: ShippingAddressForm;
  onCustomerNameChange: (v: string) => void;
  onCustomerEmailChange: (v: string) => void;
  onCustomerPhoneChange: (v: string) => void;
  onNotesChange: (v: string) => void;
  onShippingAddressChange: (field: keyof ShippingAddressForm, value: string) => void;
  onCreateDraft: () => void;
  /** Omitted on direct option checkout, which has no cart. */
  onAddItem?: () => void;
  submitLabel?: string;
  /** Direct option checkout requires a shippable address, not only a street and city. */
  requireCompleteAddress?: boolean;
  isCreating: boolean;
  error?: string | null;
}

export function ReviewStep({
  items,
  customerName,
  customerEmail,
  customerPhone,
  notes,
  shippingAddress,
  onCustomerNameChange,
  onCustomerEmailChange,
  onCustomerPhoneChange,
  onNotesChange,
  onShippingAddressChange,
  onCreateDraft,
  onAddItem,
  submitLabel = 'Create Order',
  requireCompleteAddress = false,
  isCreating,
  error,
}: ReviewStepProps) {
  const totalQty = items.reduce((sum, item) => sum + item.quantity, 0);
  const grandTotal = items.reduce(
    (sum, item) => sum + item.quantity * item.unitPrice,
    0,
  );

  const addressReady =
    shippingAddress.address1.trim() !== '' &&
    shippingAddress.city.trim() !== '' &&
    (!requireCompleteAddress ||
      (shippingAddress.zip.trim() !== '' && shippingAddress.country.trim() !== ''));
  const priceReady = items.every((item) => !item.priceUnavailable);
  const canCreate =
    items.length > 0 &&
    !isCreating &&
    priceReady &&
    customerName.trim() !== '' &&
    customerEmail.trim() !== '' &&
    addressReady;

  return (
    <div className="space-y-6">
      {/* Order Items */}
      <div className="rounded-xl border border-border bg-white overflow-hidden">
        <div className="border-b border-border bg-surface-secondary px-5 py-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
            Order Summary
          </h3>
        </div>

        <div className="divide-y divide-border">
          {items.map((item, idx) => (
            <div key={idx} className="px-5 py-4">
              <div className="flex items-start gap-4">
                <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-surface-tertiary text-xs font-bold text-text-tertiary">
                  &times;{item.quantity}
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-medium text-text-primary truncate">
                    {item.title}
                  </p>
                  {item.presetName && (
                    <p className="text-xs text-text-tertiary">{item.presetName}</p>
                  )}
                  {/* Dynamic option details */}
                  {item.selectedOptions && item.selectedOptions.length > 0 && (
                    <div className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5">
                      {item.selectedOptions.map((opt) => (
                        <span key={opt.typeSlug} className="text-xs text-text-secondary">
                          {opt.typeName}: <span className="font-medium">{opt.valueName}</span>
                        </span>
                      ))}
                    </div>
                  )}
                  {item.optionSummary && item.optionSummary.length > 0 && (
                    <div className="mt-1.5 flex flex-col gap-0.5">
                      {item.optionSummary.map((row) => (
                        <p key={`${row.label}:${row.value}`} className="text-xs text-text-secondary">
                          {row.label}: <span className="font-medium">{row.value}</span>
                        </p>
                      ))}
                    </div>
                  )}
                  <OptionUploadCards uploads={item.uploads} />
                </div>
                <div className="text-right shrink-0">
                  {item.priceUnavailable ? (
                    <p className="text-sm font-medium text-warning-800">Price unavailable</p>
                  ) : (
                    <>
                      <p className="text-sm font-semibold text-text-primary">
                        ${(item.quantity * item.unitPrice).toFixed(2)}
                      </p>
                      <p className="text-[11px] text-text-tertiary">
                        ${item.unitPrice.toFixed(2)} each
                      </p>
                    </>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="border-t-2 border-border bg-surface-secondary px-5 py-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-text-secondary">
                Total ({totalQty} {totalQty === 1 ? 'item' : 'items'})
              </p>
            </div>
            {items.some((item) => item.priceUnavailable) ? (
              <p className="text-sm font-medium text-warning-800">Price unavailable</p>
            ) : (
              <p className="text-xl font-bold text-text-primary">
                ${grandTotal.toFixed(2)}
              </p>
            )}
          </div>
        </div>
      </div>

      {onAddItem ? (
      <button
        type="button"
        className="group flex w-full items-center justify-center gap-2.5 rounded-xl border-2 border-brand-200 bg-brand-50 py-3.5 text-sm font-semibold text-brand-600 transition-all hover:border-brand-400 hover:bg-brand-100 hover:shadow-sm active:scale-[0.99]"
        onClick={onAddItem}
      >
        <span className="flex h-6 w-6 items-center justify-center rounded-full bg-brand-600 text-white transition-colors group-hover:bg-brand-700">
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
          </svg>
        </span>
        Add Another Item
      </button>
      ) : null}

      {/* Customer Contact Information */}
      <div className="rounded-xl border border-border bg-white overflow-hidden">
        <div className="border-b border-border bg-surface-secondary px-5 py-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
            Customer Contact
          </h3>
        </div>

        <div className="p-5 space-y-4">
          {/* Account email — read-only when pre-filled, editable when empty */}
          <div>
            <label className="mb-1.5 block text-sm font-medium text-text-primary">
              Account Email <span className="text-danger">*</span>
            </label>
            {customerEmail ? (
              <>
                <div className="flex h-10 items-center rounded-lg border-2 border-border bg-surface-secondary px-3 text-sm text-text-primary">
                  <svg className="mr-2 h-4 w-4 shrink-0 text-text-tertiary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M21.75 6.75v10.5a2.25 2.25 0 01-2.25 2.25h-15a2.25 2.25 0 01-2.25-2.25V6.75m19.5 0A2.25 2.25 0 0019.5 4.5h-15a2.25 2.25 0 00-2.25 2.25m19.5 0v.243a2.25 2.25 0 01-1.07 1.916l-7.5 4.615a2.25 2.25 0 01-2.36 0L3.32 8.91a2.25 2.25 0 01-1.07-1.916V6.75" />
                  </svg>
                  <span className="truncate">{customerEmail}</span>
                  <span className="ml-auto shrink-0 rounded bg-brand-50 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider text-brand-600">
                    Linked
                  </span>
                </div>
                <p className="mt-1 text-[11px] text-text-tertiary">
                  Order is linked to this account for conversion tracking.
                </p>
              </>
            ) : (
              <Input
                type="email"
                value={customerEmail}
                onChange={(e) => onCustomerEmailChange(e.target.value)}
                placeholder="customer@example.com"
                required
              />
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">
                Full Name <span className="text-danger">*</span>
              </label>
              <Input
                value={customerName}
                onChange={(e) => onCustomerNameChange(e.target.value)}
                placeholder="John Doe"
                required
              />
            </div>
            <div>
              <label className="mb-1.5 block text-sm font-medium text-text-primary">
                Phone <span className="text-xs font-normal text-text-tertiary">(optional)</span>
              </label>
              <Input
                value={customerPhone}
                onChange={(e) => onCustomerPhoneChange(e.target.value)}
                placeholder="+1 555 000 0000"
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-medium text-text-primary">
              Notes <span className="text-xs font-normal text-text-tertiary">(optional)</span>
            </label>
            <textarea
              className="w-full rounded-lg border-2 border-border bg-white px-3 py-2.5 text-sm text-text-primary placeholder:text-text-tertiary transition-colors focus:border-brand-500 focus:outline-none"
              rows={3}
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder="Any special instructions for this order..."
            />
          </div>
        </div>
      </div>

      {/* Shipping Address */}
      <div className="rounded-xl border border-border bg-white overflow-hidden">
        <div className="border-b border-border bg-surface-secondary px-5 py-3">
          <h3 className="text-xs font-semibold uppercase tracking-wider text-text-secondary">
            Shipping Address
          </h3>
        </div>
        <div className="p-5 space-y-4">
          {requireCompleteAddress && (
            <p className="text-xs text-text-tertiary">
              Street, city, ZIP, and country are required.
            </p>
          )}
          <Input
            label="Full Name"
            value={shippingAddress.name}
            onChange={(e) => onShippingAddressChange('name', e.target.value)}
            placeholder="Recipient name"
          />
          <Input
            label="Address Line 1"
            value={shippingAddress.address1}
            onChange={(e) => onShippingAddressChange('address1', e.target.value)}
            placeholder="123 Main St"
          />
          <Input
            label="Address Line 2"
            value={shippingAddress.address2}
            onChange={(e) => onShippingAddressChange('address2', e.target.value)}
            placeholder="Apt, suite, unit (optional)"
          />
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="City"
              value={shippingAddress.city}
              onChange={(e) => onShippingAddressChange('city', e.target.value)}
              placeholder="Houston"
            />
            <Input
              label="State / Province"
              value={shippingAddress.province}
              onChange={(e) => onShippingAddressChange('province', e.target.value)}
              placeholder="TX"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <Input
              label="ZIP / Postal Code"
              value={shippingAddress.zip}
              onChange={(e) => onShippingAddressChange('zip', e.target.value)}
              placeholder="77001"
            />
            <Input
              label="Country"
              value={shippingAddress.country}
              onChange={(e) => onShippingAddressChange('country', e.target.value)}
              placeholder="US"
            />
          </div>
          <Input
            label="Phone"
            value={shippingAddress.phone}
            onChange={(e) => onShippingAddressChange('phone', e.target.value)}
            placeholder="+1 555-123-4567"
            hint="Used for shipping carrier notifications"
          />
        </div>
      </div>

      {/* Error */}
      {error && (
        <div className="flex items-center gap-3 rounded-xl border border-danger/20 bg-danger-light px-4 py-3">
          <svg className="h-5 w-5 shrink-0 text-danger" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <p className="text-sm text-danger-dark">{error}</p>
        </div>
      )}

      {/* Submit */}
      <div className="flex items-center justify-between rounded-xl border border-border bg-surface-secondary px-5 py-4">
        <div>
          <p className="text-sm text-text-secondary">
            Ready to create your order?
          </p>
          <p className="text-xs text-text-tertiary">
            Provider assignment will begin automatically.
          </p>
        </div>
        <Button
          variant="primary"
          size="lg"
          onClick={onCreateDraft}
          loading={isCreating}
          disabled={!canCreate}
        >
          {isCreating ? 'Creating...' : submitLabel}
        </Button>
      </div>
    </div>
  );
}
